import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Modal as MuiModal,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import Image from 'next/image';

import { nutritionUnits } from '@utils/nutritionUnits';
import {
  updateCartItemCount,
  updatePantryCartItemCount,
  usePersistentCartItems,
  usePersistentPantryCartItems,
} from 'store/cartItems';
import theme from 'src/style-system/theme';

import ImageCarousel from '@components/ImageCarousel';
import Accordion from '@components/Accordian';
import CuminButton from '@components/Button';
import useClickOutside from '@utils/useClickOutside';
import { productType, nutritionType } from '@utils/types';

import { AppRoutes } from '@utils/appRoutes';
import useAuth from '@utils/useAuth';
import ArrowTooltips from '@components/Tooltip';
import { GetFilledChilli } from '@components/FilledChilli';
import { GetEmptyChilli } from '@components/EmptyChilli';
import { GetVegan } from '@components/Vegan';

import { useStyles } from './useStyles';

interface ModalProps {
  open: boolean;
  handleClose: () => void;
  product: productType;
  isSubscriptionBtn?: boolean;
  isNoAddMealBtn?: boolean;
  isPantry?: boolean;
  mode?: string;
  elevarAddToCartEvent?: any;
  elevarRemoveFromCartEvent?: any;
  elevarViewItemEvent?: any;
}

const DishModal = ({
  open,
  handleClose,
  product,
  isSubscriptionBtn,
  isNoAddMealBtn,
  isPantry = false,
  mode,
  elevarAddToCartEvent,
  elevarRemoveFromCartEvent,
  elevarViewItemEvent,
}: ModalProps) => {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const cartItems = usePersistentCartItems();
  const pantryCartItems = usePersistentPantryCartItems();
  const { detectedCountryCode: countryCode, authState } = useAuth();

  useEffect(() => {
    if (product.id && !mode && elevarViewItemEvent) {
      elevarViewItemEvent(product);
    }
  }, [product.id, authState.token, mode]);

  const decrement = (productId: number, productTitle: string) => {
    if (cartItems[productId] === 1) {
      //@ts-ignore
      window?.dataLayer.push({
        text: productTitle,
        event: 'item_remove',
      });
    }

    if (!mode && elevarRemoveFromCartEvent) {
      //for elvart remove from cart event
      elevarRemoveFromCartEvent(product);
    }

    if (isPantry) {
      if (pantryCartItems[productId]) {
        updatePantryCartItemCount(productId, pantryCartItems[productId] - 1);
      }
    } else {
      if (cartItems[productId]) {
        updateCartItemCount(productId, cartItems[productId] - 1);
      }
    }
  };
  const increment = (productId: number) => {
    if (!mode && elevarAddToCartEvent) {
      //fire elevar add to cart event
      elevarAddToCartEvent(product);
    }

    if (isPantry) {
      if (pantryCartItems[productId]) {
        updatePantryCartItemCount(productId, pantryCartItems[productId] + 1);
      } else {
        updatePantryCartItemCount(productId, 1);
      }
    } else {
      if (cartItems[productId]) {
        updateCartItemCount(productId, cartItems[productId] + 1);
      } else {
        updateCartItemCount(productId, 1);
      }
    }
  };

  useClickOutside(ref, () => {
    handleClose();
  });

  const onStartSubscription = () => {
    handleClose();
    router.push(`/${countryCode}${AppRoutes.ORDER_STEP_TWO}`);
  };

  const getMealCount = useMemo(() => {
    return (
      (isPantry ? pantryCartItems[product?.id] : cartItems[product?.id]) || 0
    );
  }, [pantryCartItems, cartItems]);

  const isDecIconButtonDisabled = useMemo(() => {
    return isPantry
      ? !pantryCartItems[product?.id] || pantryCartItems[product?.id] === 0
      : !cartItems[product?.id] || cartItems[product?.id] === 0;
  }, [pantryCartItems, cartItems]);

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className={classes.container} id="dish-info-modal">
        <Box className={classes.mainContainer} ref={ref}>
          <Box className={classes.iconWrapper}>
            <CloseIcon className={classes.closeIcon} onClick={handleClose} />
          </Box>
          {product?.secondaryImages &&
          Array.isArray(product?.secondaryImages) ? (
            <Box className={classes.carouselContainer}>
              <ImageCarousel carouselContent={product?.secondaryImages} />
            </Box>
          ) : (
            <Box className={classes.imgContainer}>
              {product?.image_url ? (
                <Box className={classes.img}>
                  <Image
                    src={product?.image_url}
                    alt={product?.requires}
                    objectFit="cover"
                    layout="fill"
                  />
                </Box>
              ) : null}
            </Box>
          )}

          <Box className={classes.contentWrapper}>
            <Typography
              variant="h2"
              align="center"
              color="cinnamon.main"
              mb={2}
            >
              {product?.title}
            </Typography>
            {product?.side?.title ? (
              <Typography
                variant="body2"
                align="center"
                color="cumin.main"
                className={classes.sides}
              >
                Recommended Side: {product?.side?.title}
              </Typography>
            ) : null}
            {product?.requires ? (
              <Typography
                variant="body2"
                align="center"
                color="cumin.main"
                className={classes.requires}
              >
                Requirement: {product?.requires}
              </Typography>
            ) : null}

            <Typography variant="body2" align="center" color="cumin.main">
              {product?.description}
            </Typography>
            {product?.spice_level !== 0 ? (
              <Box className={classes.symbolWrapper}>
                {product?.vegan ? (
                  <ArrowTooltips title="Vegan">
                    <GetVegan />
                  </ArrowTooltips>
                ) : null}
                <ArrowTooltips title="Spice Level">
                  <>
                    {product?.spice_level >= 1 ? (
                      <GetFilledChilli />
                    ) : (
                      <GetEmptyChilli />
                    )}
                    {product?.spice_level >= 2 ? (
                      <GetFilledChilli />
                    ) : (
                      <GetEmptyChilli />
                    )}
                    {product?.spice_level >= 3 ? (
                      <GetFilledChilli />
                    ) : (
                      <GetEmptyChilli />
                    )}
                    {product?.spice_level >= 4 ? (
                      <GetFilledChilli />
                    ) : (
                      <GetEmptyChilli />
                    )}
                    {product?.spice_level >= 5 ? (
                      <GetFilledChilli />
                    ) : (
                      <GetEmptyChilli />
                    )}
                  </>
                </ArrowTooltips>
              </Box>
            ) : product?.vegan ? (
              <Box className={classes.symbolWrapper}>
                {product?.vegan ? (
                  <ArrowTooltips title="Vegan">
                    <GetVegan />
                  </ArrowTooltips>
                ) : null}
              </Box>
            ) : null}
            {isSubscriptionBtn ? (
              <Box className={classes.startSubscriptionBtnWrapper}>
                <CuminButton
                  color="turmeric"
                  variant="contained"
                  textColor="garamMasala.main"
                  width="250px"
                  height="48"
                  borderRadius="24"
                  onClick={onStartSubscription}
                >
                  Start a Subscription
                </CuminButton>
              </Box>
            ) : null}

            {isNoAddMealBtn ? null : (
              <Box className={classes.wrapper}>
                <Box className={classes.btnContainer}>
                  <IconButton
                    onClick={() => decrement(product?.id, product?.title)}
                    size="large"
                    disabled={isDecIconButtonDisabled}
                  >
                    <RemoveCircleIcon
                      fontSize="inherit"
                      sx={{
                        color: !isDecIconButtonDisabled
                          ? 'cinnamon.main'
                          : 'lightGray.main',
                      }}
                    />
                  </IconButton>

                  <Box
                    sx={{
                      flexGrow: 4,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" color="cinnamon.main">
                      {getMealCount}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={() => increment(product?.id)}
                    size="large"
                  >
                    <AddCircleIcon
                      sx={{ color: 'cinnamon.main' }}
                      fontSize="inherit"
                    />
                  </IconButton>
                </Box>
              </Box>
            )}

            {product?.servingSize ? (
              <Accordion
                bgcolor={theme.palette.whitePepper.main}
                bordercolor={theme.palette.cumin.main}
                marginbottom={15}
              >
                <AccordionSummary
                  expandIcon={
                    <ArrowDropDownIcon
                      fontSize="large"
                      sx={{ color: 'garamMasala.main' }}
                    />
                  }
                  aria-controls="panel1a-content"
                >
                  <Typography
                    variant="body1"
                    align="left"
                    color="cinnamon.main"
                  >
                    Serving Size
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography
                      variant="body2"
                      align="justify"
                      color="cumin.main"
                    >
                      {product?.servingSize}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : null}

            <Accordion
              bgcolor={theme.palette.whitePepper.main}
              bordercolor={theme.palette.cumin.main}
              marginbottom={15}
            >
              <AccordionSummary
                expandIcon={
                  <ArrowDropDownIcon
                    fontSize="large"
                    sx={{ color: 'garamMasala.main' }}
                  />
                }
                aria-controls="panel1a-content"
              >
                <Typography variant="body1" align="left" color="cinnamon.main">
                  Ingredients
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography
                    variant="body2"
                    align="justify"
                    color="cumin.main"
                  >
                    {product?.ingredients}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion
              bgcolor={theme.palette.whitePepper.main}
              bordercolor={theme.palette.cumin.main}
              marginbottom={15}
            >
              <AccordionSummary
                expandIcon={
                  <ArrowDropDownIcon
                    fontSize="large"
                    sx={{ color: 'garamMasala.main' }}
                  />
                }
                aria-controls="panel1a-content"
              >
                <Typography variant="body1" align="left" color="cinnamon.main">
                  Nutrition Facts
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {product?.servingsPerContainer ? (
                    <Typography
                      variant="body2"
                      align="justify"
                      color="cumin.main"
                      mb="2px"
                    >
                      {product?.servingsPerContainer}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    align="justify"
                    color="cumin.main"
                    mb="10px"
                  >
                    Per serving nutrition facts:
                  </Typography>
                  <Typography
                    variant="body2"
                    align="justify"
                    color="cumin.main"
                  >
                    {product?.nutrition
                      ?.filter((item: nutritionType) => item?.value > 0)
                      ?.map(
                        (item: nutritionType) =>
                          ` ${item?.name} ${item?.value}${
                            nutritionUnits[item?.name]
                          }`
                      )
                      ?.toString()}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion
              bgcolor={theme.palette.whitePepper.main}
              bordercolor={theme.palette.cumin.main}
              marginbottom={15}
            >
              <AccordionSummary
                expandIcon={
                  <ArrowDropDownIcon
                    fontSize="large"
                    sx={{ color: 'garamMasala.main' }}
                  />
                }
                aria-controls="panel1a-content"
              >
                <Typography variant="body1" align="left" color="cinnamon.main">
                  How to Prepare
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography
                    variant="body2"
                    align="justify"
                    color="cumin.main"
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product?.how_to_prepare,
                      }}
                    ></div>
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </MuiModal>
  );
};

export default DishModal;
